import { BlobClient } from '@frontend/blob/api';
import { EntityType, fileDownload } from '@frontend/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { JobClient } from '@frontend/report/api';
import { JobStateBadge } from '@frontend/report/badge';
import { Job, JobStateName } from '@frontend/report/types';
import { FormattedMessage } from 'react-intl';

import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { useHistoryList } from './history-list.controller';

const ID = 'report-history-list';

export interface HistoryListProps {
    report_id: string;
    user_id?: string;
}
const HistoryList = (props: HistoryListProps) => {
    const viewProps = useHistoryList(props);

    return (
        <ReactiveObjectList<Job>
            id={ID}
            type={EntityType.JOB}
            tableProps={{
                maxHeight: true,
                hideFooter: true
            }}
            cardClassName='shrink-0'
            eventListener={EntityType.JOB}
            autoRefreshMode='on'
            useStaticListId
            sortingColumns={[]}
            dispatch={viewProps.dispatch}
            fetch={(arg) => JobClient.fetchJobs({ ...arg, report_id: props.report_id, user_id: props.user_id, size: '5' })}
            label={
                props.user_id ? (
                    <FormattedMessage
                        id='report-detail-modal.my-report-history'
                        defaultMessage='My report history'
                    />
                ) : (
                    <FormattedMessage
                        id='report-detail-modal.generation-history'
                        defaultMessage='Generation history'
                    />
                )
            }
            columns={{
                columns: ['creation_timestamp', 'generated_by', 'status'],
                headerOverwrite: new Map([
                    ['creation_timestamp', CommonMessage.OBJECTS.JOB.GENERATED_AT],
                    ['generated_by', CommonMessage.OBJECTS.JOB.GENERATED_BY]
                ]),
                customCells: new Map([
                    [
                        'status',
                        (obj) =>
                            obj.row.original.status ? (
                                <td className={defaultCellClassName}>
                                    <div className='flex items-center justify-between'>
                                        <JobStateBadge state={obj.row.original.status} />
                                        {obj.row.original.output && (
                                            <button
                                                className='cursor-pointer'
                                                disabled={obj.row.original.status !== JobStateName.PROCESSED}
                                                onClick={async () => {
                                                    if (obj.row.original.status !== JobStateName.PROCESSED) return;

                                                    const { blob, filename } = await BlobClient.fetchBlobWithFilename(
                                                        obj.row.original.account_id,
                                                        EntityType.REPORT_JOB,
                                                        obj.row.original.id
                                                    );
                                                    if (!blob) return;
                                                    fileDownload(blob, filename ?? undefined);
                                                }}>
                                                <ICONS.BUTTON.DOWNLOAD
                                                    className={`mr-7 size-5 ${obj.row.original.status === JobStateName.PROCESSED ? 'text-foreground-default hover:text-brand-primary' : 'text-brand-muted hover:cursor-not-allowed'}`}
                                                />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            ) : (
                                <td></td>
                            )
                    ],
                    [
                        'generated_by',
                        (obj) => {
                            if (obj.row.original.user_id) {
                                return (
                                    <td className={defaultCellClassName}>
                                        <PlatformObjectBadge
                                            entityType={EntityType.USER}
                                            id={obj.row.original.user_id}
                                            key={obj.row.original.user_id}
                                        />
                                    </td>
                                );
                            } else if (obj.row.original.schedule_id) {
                                return (
                                    <td className={defaultCellClassName}>
                                        <PlatformObjectBadge
                                            entityType={EntityType.SCHEDULE}
                                            id={obj.row.original.schedule_id}
                                            key={obj.row.original.schedule_id}
                                        />
                                    </td>
                                );
                            }
                        }
                    ]
                ])
            }}
        />
    );
};

export default HistoryList;
